import styled, { keyframes } from 'styled-components';
import { theme } from '../../styles/theme';

// ===== ANIMATIONS =====

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ===== LAYOUT =====

export const BlogPage = styled.div`
  min-height: 100vh;
  background: ${theme.colors.bgPrimary};
  color: ${theme.colors.textPrimary};
  font-family: ${theme.fonts.sans};
  -webkit-font-smoothing: antialiased;
  padding-top: 96px;
`;

export const BlogContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.sm};
  }
`;

export const BlogHeader = styled.header`
  text-align: center;
  padding: ${theme.spacing.xl} 0 ${theme.spacing.xxl};
  border-bottom: 1px solid ${theme.colors.border};
  margin-bottom: ${theme.spacing.xxl};
`;

export const BlogPreTitle = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${theme.colors.accentWarm};
  margin-bottom: ${theme.spacing.sm};
`;

export const BlogTitle = styled.h1`
  font-family: ${theme.fonts.serif};
  font-weight: 300;
  letter-spacing: -0.03em;
  font-size: 64px;
  line-height: 1.1;
  margin: 0 0 ${theme.spacing.md};
  color: ${theme.colors.textPrimary};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 40px;
  }
`;

export const BlogSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.5;
  color: ${theme.colors.textSecondary};
  max-width: 640px;
  margin: 0 auto;
`;

// ===== CATEGORIA PILLS =====

export const CategoriaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.lg};
`;

export const CategoriaPill = styled.a`
  display: inline-block;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: ${({ $active }) => ($active ? '#FFFFFF' : theme.colors.textSecondary)};
  background: ${({ $active }) => ($active ? theme.colors.bgDark : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? theme.colors.bgDark : theme.colors.border)};
  border-radius: 999px;
  cursor: pointer;
  text-decoration: none;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${({ $active }) => ($active ? theme.colors.bgDark : theme.colors.bgSection)};
    border-color: ${theme.colors.textPrimary};
    color: ${({ $active }) => ($active ? '#FFFFFF' : theme.colors.textPrimary)};
  }
`;

// ===== HERO POST (post em destaque no topo da listagem) =====

export const HeroPost = styled.a`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xxl};
  padding-bottom: ${theme.spacing.xxl};
  border-bottom: 1px solid ${theme.colors.border};
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  animation: ${fadeUp} 600ms ease-out;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
  }

  &:hover img {
    transform: scale(1.02);
  }
`;

export const HeroImageWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: ${theme.colors.bgSection};
`;

export const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform ${theme.transitions.slow};
`;

export const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const HeroPostTitle = styled.h2`
  font-family: ${theme.fonts.serif};
  font-weight: 300;
  letter-spacing: -0.02em;
  font-size: 44px;
  line-height: 1.15;
  margin: ${theme.spacing.sm} 0 ${theme.spacing.md};
  color: ${theme.colors.textPrimary};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 30px;
  }
`;

export const HeroPostExcerpt = styled.p`
  font-size: 17px;
  line-height: 1.6;
  color: ${theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing.md};
`;

// ===== GRID DE POSTS =====

export const PostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.xl} ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.xxl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
  }
`;

export const PostCardWrap = styled.a`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  animation: ${fadeUp} 500ms ease-out;
  animation-fill-mode: backwards;
  animation-delay: ${({ $i }) => ($i || 0) * 60}ms;

  &:hover img {
    transform: scale(1.03);
  }
`;

export const PostCardImage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: ${theme.colors.bgSection};
  margin-bottom: ${theme.spacing.md};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${theme.transitions.slow};
  }
`;

export const PostCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${theme.colors.accentWarm};
  margin-bottom: ${theme.spacing.xs};
`;

export const PostCardTitle = styled.h3`
  font-family: ${theme.fonts.serif};
  font-weight: 400;
  letter-spacing: -0.01em;
  font-size: 22px;
  line-height: 1.25;
  margin: 0 0 ${theme.spacing.xs};
  color: ${theme.colors.textPrimary};
`;

export const PostCardExcerpt = styled.p`
  font-size: 15px;
  line-height: 1.55;
  color: ${theme.colors.textSecondary};
  margin: 0;
`;

// ===== POST PAGE (single article) =====

export const PostPageWrap = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: ${theme.spacing.xl};
  max-width: 1440px;
  margin: 0 auto;
  padding: ${theme.spacing.xl} ${theme.spacing.lg} ${theme.spacing.section};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: minmax(0, 1fr);
    padding: ${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.xxl};
  }
`;

export const PostHeader = styled.header`
  text-align: left;
  margin-bottom: ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border};
`;

export const PostHeaderMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.colors.accentWarm};
  margin-bottom: ${theme.spacing.md};
`;

export const PostMetaSep = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${theme.colors.textMuted};
`;

export const PostTitle = styled.h1`
  font-family: ${theme.fonts.serif};
  font-weight: 300;
  letter-spacing: -0.025em;
  font-size: 52px;
  line-height: 1.1;
  margin: 0 0 ${theme.spacing.md};
  color: ${theme.colors.textPrimary};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 34px;
  }
`;

export const PostSubtitle = styled.p`
  font-size: 20px;
  line-height: 1.5;
  color: ${theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing.lg};
  font-weight: 300;
`;

export const PostCover = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: ${theme.colors.bgSection};
  margin-bottom: ${theme.spacing.xl};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// estilo do conteúdo markdown renderizado
export const PostContent = styled.article`
  font-size: 18px;
  line-height: 1.75;
  color: ${theme.colors.textPrimary};

  p {
    margin: 0 0 ${theme.spacing.md};
  }

  h2 {
    font-family: ${theme.fonts.serif};
    font-weight: 400;
    letter-spacing: -0.015em;
    font-size: 32px;
    line-height: 1.25;
    margin: ${theme.spacing.xl} 0 ${theme.spacing.md};
    color: ${theme.colors.textPrimary};
    scroll-margin-top: 96px;

    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 26px;
    }
  }

  h3 {
    font-family: ${theme.fonts.sans};
    font-weight: 600;
    font-size: 20px;
    margin: ${theme.spacing.lg} 0 ${theme.spacing.sm};
    color: ${theme.colors.textPrimary};
  }

  ul, ol {
    padding-left: 1.4em;
    margin: 0 0 ${theme.spacing.md};
  }

  li {
    margin-bottom: ${theme.spacing.xs};
  }

  blockquote {
    border-left: 3px solid ${theme.colors.accent};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    margin: ${theme.spacing.lg} 0;
    color: ${theme.colors.textSecondary};
    font-style: italic;
    background: ${theme.colors.bgSection};
  }

  a {
    color: ${theme.colors.textPrimary};
    text-decoration: underline;
    text-decoration-color: ${theme.colors.accent};
    text-underline-offset: 3px;
    transition: color ${theme.transitions.fast};

    &:hover {
      color: ${theme.colors.accent};
    }
  }

  strong {
    font-weight: 600;
    color: ${theme.colors.textPrimary};
  }

  img {
    max-width: 100%;
    height: auto;
    margin: ${theme.spacing.lg} 0;
  }

  hr {
    border: none;
    border-top: 1px solid ${theme.colors.border};
    margin: ${theme.spacing.xl} 0;
  }

  code {
    background: ${theme.colors.bgSection};
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.92em;
  }
`;

// ===== TOC (sumário lateral sticky) =====

export const TOCWrap = styled.aside`
  position: sticky;
  top: 120px;
  align-self: start;
  font-size: 13px;
  line-height: 1.5;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const TOCTitle = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.accentWarm};
  margin-bottom: ${theme.spacing.sm};
`;

export const TOCList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  border-left: 1px solid ${theme.colors.border};
`;

export const TOCItem = styled.li`
  a {
    display: block;
    padding: 6px 12px;
    color: ${theme.colors.textSecondary};
    text-decoration: none;
    border-left: 2px solid transparent;
    margin-left: -1px;
    transition: all ${theme.transitions.fast};

    &:hover {
      color: ${theme.colors.textPrimary};
      border-left-color: ${theme.colors.accent};
    }
  }
`;

// ===== AUTHOR / FOOTER DO POST =====

export const PostAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.lg} 0;
  border-top: 1px solid ${theme.colors.border};
  margin-top: ${theme.spacing.xl};
  font-size: 14px;
  color: ${theme.colors.textSecondary};

  strong {
    color: ${theme.colors.textPrimary};
  }
`;

// bloco "imóveis relacionados" no rodapé do post
export const RelatedSection = styled.section`
  background: ${theme.colors.bgSection};
  padding: ${theme.spacing.xxl} 0;
  margin-top: ${theme.spacing.xxl};
`;

export const RelatedTitle = styled.h3`
  font-family: ${theme.fonts.serif};
  font-weight: 300;
  letter-spacing: -0.02em;
  font-size: 32px;
  margin: 0 0 ${theme.spacing.lg};
  color: ${theme.colors.textPrimary};
  text-align: center;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 24px;
  }
`;

// ===== EMPTY STATE =====

export const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xxl} ${theme.spacing.md};
  color: ${theme.colors.textSecondary};

  h3 {
    font-family: ${theme.fonts.serif};
    font-weight: 300;
    font-size: 28px;
    margin: 0 0 ${theme.spacing.sm};
    color: ${theme.colors.textPrimary};
  }
`;

// ===== LOADING =====

export const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  color: ${theme.colors.textMuted};
  font-size: 14px;
  letter-spacing: 0.05em;
`;
